import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { isEqualWith } from "lodash";

import { StatusCode } from "api/protocols";

import { showError } from "redux/actions/ui";
import { formatDate } from "helpers/Helpers";
import { useAppDispatch } from "hooks/redux";

import Page from "components/page/Page";
import Card from "components/card/Card";
import Input from "components/form/input/Input";
import FormLayout from "components/form/FormLayout";
import TextField from "components/form/textField/TextField";
import { Select } from "components/select";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import {
  GetReservationNotification,
  PutReservationNotification,
} from "api/rpc/2024-04/facilityAdmin/reservation/reservation";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { MOBILE_WIDTH, TABLET_WIDTH } from "helpers/ScreenSizes";

interface INotificationForm {
  id: string | null;
  module_id: number | null;
  start_date: string;
  end_date: string;
  date_range: string[];
  dateRangeStart: Date;
  dateRangeEnd: Date;
  user_type: string | undefined;
  subject: string;
  content: string;
  type: string | undefined;
  module_title: string;
}

export default function TeeSheetNotification() {
  const { notificationId } = useParams<{ notificationId: string }>();
  const { t } = useTranslation();
  const windowSize = useWindowSize();
  const history = useHistory();

  const dispatch = useAppDispatch();

  function formatDateRange(startingDate: Date, endingDate: Date) {
    return [formatDate(startingDate), formatDate(endingDate) + " 23:59:00"];
  }

  const today = new Date();

  const [notificationState, setNotificationState] = useState<{
    prevSaveState: INotificationForm | undefined;
    prevStartingDate: Date;
    prevEndingDate: Date;
    deletePopupVisibile: boolean;
  }>({
    prevSaveState: undefined,
    prevStartingDate: today,
    prevEndingDate: today,
    deletePopupVisibile: false,
  });

  const [saveState, setSaveState] = useState<INotificationForm>({
    id: null,
    module_id: null,
    start_date: new Date().toISOString().slice(0, 10),
    end_date: new Date().toISOString().slice(0, 10),
    date_range: formatDateRange(today, today),
    dateRangeStart: today,
    dateRangeEnd: today,
    user_type: undefined,
    subject: "",
    content: "",
    type: undefined,
    module_title: "",
  });

  // populate component
  useEffect(() => {
    if (saveState.id === null) {
      void loadNotification();
    }
  }, [notificationId]);

  async function loadNotification() {
    const tempRes = await GetReservationNotification({ id: Number(notificationId) }, true);
    if (tempRes.status !== StatusCode.OK || !Array.isArray(tempRes.data) || !tempRes.data[0]) {
      return;
    }

    const notification = tempRes.data[0];
    const startDate = notification.start_date == null ? "" : String(notification.start_date);
    const endDate = notification.end_date == null ? "" : String(notification.end_date);
    const startingDate = dateFromFormattedString(startDate);
    const endingDate = dateFromFormattedString(endDate);
    const tempState: INotificationForm = {
      module_id: Number(notification.module_id),
      start_date: startDate,
      end_date: endDate,
      dateRangeStart: startingDate,
      dateRangeEnd: endingDate,
      date_range: formatDateRange(startingDate, endingDate),
      id: notificationId,
      subject: notification.subject == null ? "" : String(notification.subject),
      content: notification.content == null ? "" : String(notification.content),
      user_type: notification.user_type == null ? undefined : String(notification.user_type),
      type: notification.type == null ? undefined : String(notification.type),
      module_title: notification.module_title == null ? "" : String(notification.module_title),
    };

    setSaveState({ ...saveState, ...tempState });
    setNotificationState({
      ...notificationState,
      prevSaveState: tempState,
      prevStartingDate: startingDate,
      prevEndingDate: endingDate,
    });
  }

  async function saveNotification() {
    const params = {
      start_date: saveState.start_date,
      end_date: saveState.end_date,
      id: saveState.id,
      user_type: saveState.user_type,
      subject: saveState.subject,
      content: saveState.content,
      type: saveState.type,
    };

    const res = await PutReservationNotification(params, true);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(typeof res.data === "string" ? res.data : t("Error saving notification")));
      return;
    }
    history.push("/admin/settings/reservations/notifications");
  }

  function unsavedChangesExist() {
    if (notificationState.prevSaveState === undefined) {
      if (loadNotification) {
        setNotificationState({
          ...notificationState,
          prevSaveState: saveState,
        });
      }
      return false;
    }
    return !isEqualWith(notificationState.prevSaveState, saveState);
  }

  function cancelUnsavedChanges() {
    if (!notificationState.prevSaveState) {
      return;
    }
    setSaveState(notificationState.prevSaveState);
  }

  useEffect(() => {
    if (saveState.dateRangeStart && saveState.dateRangeEnd) {
      const date_range = formatDateRange(saveState.dateRangeStart, saveState.dateRangeEnd);
      setSaveState(prevState => ({ ...prevState, date_range }));
    }
  }, [saveState.dateRangeStart, saveState.dateRangeEnd]);

  function setDateRangeStart(date: Date) {
    const startDateAsString = new Date(date).toISOString().slice(0, 10);

    setSaveState(prevState => ({
      ...prevState,
      dateRangeStart: date,
      start_date: startDateAsString,
    }));
  }

  function setDateRangeEnd(date: Date) {
    const dateAsString = new Date(date).toISOString().slice(0, 10);

    setSaveState(prevState => ({
      ...prevState,
      dateRangeEnd: date,
      end_date: dateAsString,
    }));
  }

  return (
    <Page
      title={`${t("Activity Notification")} #${notificationId}`}
      subtitle={saveState.module_title}
      narrow
      breadcrumbs={[
        {
          prefix: true,
          label: t("Back to Notifications"),
          url: "/admin/settings/reservations/notifications",
        },
      ]}
      notificationBarProps={{
        isVisible: unsavedChangesExist(),
        onAction: saveNotification,
        onCancel: cancelUnsavedChanges,
      }}
    >
      <Card>
        <Card.Section>
          <FormLayout>
            <FormLayout.Group>
              <div id="notification-date-selector" style={{ marginRight: "15px" }}>
                <DatePickerInput
                  label={t("Notification Date")}
                  months={windowSize.width > TABLET_WIDTH ? 2 : 1}
                  position="left"
                  showQuickOptions={windowSize.width > MOBILE_WIDTH}
                  startingDate={saveState.dateRangeStart}
                  setStartingDate={setDateRangeStart}
                  endingDate={saveState.dateRangeEnd}
                  setEndingDate={setDateRangeEnd}
                />
              </div>

              <div></div>
            </FormLayout.Group>

            <FormLayout.Group>
              <Select
                defaultValue={saveState.user_type}
                label={t("User Type")}
                onChange={(value: number) => setSaveState(prev => ({ ...prev, user_type: value }))}
              >
                {notificationUserTypes(t).map((type, index: number) => {
                  return (
                    <Select.Option key={index} value={type.normalized} name={type.title}>
                      {type.title}
                    </Select.Option>
                  );
                })}
              </Select>

              <Select
                label={t("Type")}
                defaultValue={saveState.type}
                onChange={(value: number) => setSaveState(prev => ({ ...prev, type: value }))}
              >
                <Select.Option name={"forced"} value={"forced"}>
                  {t("Forced")}
                </Select.Option>{" "}
                <Select.Option value={"passive"} name={"passive"}>
                  {t("Passive")}
                </Select.Option>
              </Select>
            </FormLayout.Group>
            <FormLayout.Group>
              <Input
                value={saveState.subject}
                label={t("secure.facility.settings.reservations.notifications.reservation_notifications_edit.010")}
                id="subject"
                onChange={e => setSaveState(prev => ({ ...prev, subject: e.target.value }))}
                placeholder={t(
                  "secure.facility.settings.reservations.notifications.reservation_notifications_edit.011",
                )}
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <TextField
                value={saveState.content}
                label={t("secure.facility.settings.reservations.notifications.reservation_notifications_edit.012")}
                id="content"
                onChange={e => setSaveState(prev => ({ ...prev, content: e.target.value }))}
                placeholder={t(
                  "secure.facility.settings.reservations.notifications.reservation_notifications_edit.007",
                )}
              />
            </FormLayout.Group>
          </FormLayout>
        </Card.Section>
      </Card>
    </Page>
  );
}

/** Formatted String ==> YYYY-MM-DD */
function dateFromFormattedString(formattedString: string): Date {
  const tempDateArray = formattedString.split("-");

  try {
    const returnDate = new Date(Number(tempDateArray[0]), Number(tempDateArray[1]) - 1, Number(tempDateArray[2]));
    return returnDate;
  } catch {
    return new Date();
  }
}

const notificationUserTypes = (t: TFunction<"translation", undefined>) => {
  return [
    {
      title: t("Admin"),
      normalized: "admin",
    },
    {
      title: t("Customer"),
      normalized: "customer",
    },
  ];
};
